/* eslint-disable max-lines-per-function */
import { Termin } from '@/domain/termine/shared/types/Termin';
import dayjs from 'dayjs';
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { DayCellHeader } from '@/domain/termine/features/Calendar/Grid/DayCell/DayCellHeader';
import { generateWiederholungen } from '@/domain/termine/shared/utils/generateWiederholungen';
import { cn } from '@/lib/utils';
import { DayCellTabletEntries } from '@/domain/termine/features/Calendar/Grid/DayCell/DayCellTabletEntries';
import { DayCellDesktopEntries } from '@/domain/termine/features/Calendar/Grid/DayCell/DayCellDesktopEntries';
import { DayCellMobileEntries } from '@/domain/termine/features/Calendar/List/DayCell/DayCellMobileEntries';
import { Button } from '@/components/ui/button';
import { faPlusLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams, useNavigate } from 'react-router-dom';
import { YearMonthParams } from '@/domain/termine/type';

interface DayCellProps {
  day: dayjs.Dayjs;
  termine: Termin[];
}

function splitTermine(events: Termin[], visibleCount: number) {
  return {
    visible: events.slice(0, visibleCount),
    hidden: events.slice(visibleCount),
  };
}

export function DayCell({ day, termine }: DayCellProps) {
  const { startDate, endDate, standortId } = useCalendarContext();
  const { year, month } = useParams<YearMonthParams>();
  const navigate = useNavigate();

  const standortTermine = standortId
    ? termine.filter((t) => t.idStandort === standortId)
    : termine;

  const alleTermine = standortTermine.flatMap((termin) =>
    generateWiederholungen(termin, startDate, endDate),
  );

  const termineForDay = alleTermine.filter((t) =>
    day.isSame(dayjs(t.datum), 'day'),
  );

  const desktopTermine = splitTermine(termineForDay, 2);
  const tabletTermine = splitTermine(termineForDay, 1);

  const sheetUrl = `/termine/uebersicht/${year}/${month}/create/${day.format(
    'YYYY-MM-DD',
  )}`;

  return (
    <div
      className={cn(
        'text-end p-0 md:p-1 text-sm cursor-pointer',
        'text-neutrals-N900 border-neutrals-N300',
        'border-r last:border-r-0 md:min-h-40',
        'group',
      )}
      onClick={() => navigate(sheetUrl)}
      role="button"
      tabIndex={0}>
      <div className="flex flex-col h-full relative">
        {termineForDay.length === 0 && (
          <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
            <Button
              variant="default"
              className={cn(
                'h-10 w-10 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-auto',
              )}
              size="icon"
              aria-label="Termin anlegen"
              tabIndex={-1}>
              <FontAwesomeIcon icon={faPlusLarge} />
            </Button>
          </div>
        )}
        {/* Mobile (sm only) */}
        <div className="flex flex-col sm:hidden">
          <DayCellMobileEntries termine={termineForDay} day={day} />
        </div>
        {/* Tablet (md only) */}
        <div className="flex-col hidden sm:block">
          <DayCellHeader day={day} termine={termineForDay} />
          <DayCellTabletEntries
            visible={tabletTermine.visible}
            hidden={tabletTermine.hidden}
            day={day}
          />
          {/* Desktop (lg and above) */}
          <DayCellDesktopEntries
            visible={desktopTermine.visible}
            hidden={desktopTermine.hidden}
            day={day}
          />
        </div>
      </div>
    </div>
  );
}
